/**
 * AI Chat Context
 * Manages AI chatbot state and provides chat functionality throughout the app
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type {
  AIChatContextValue,
  AISessionState,
  AIChatMessage,
  AIAction,
  AIGenerateReportRequest,
  AIRefineConfigRequest,
  AIAnalyzePDFRequest,
} from '../types';
import { aiChatService } from '../services/aiChatService';

const AIChatContext = createContext<AIChatContextValue | undefined>(undefined);

interface AIChatProviderProps {
  children: ReactNode;
  onNavigate?: (target: string, data?: any) => void;
  onSave?: (preview: any, reportType: string) => Promise<string | null>;
  appContext?: 'system' | 'tenant';
}

export function AIChatProvider({ children, onNavigate, onSave, appContext }: AIChatProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState<AISessionState | null>(null);

  const openChat = useCallback(() => {
    setIsOpen(true);
    if (!session) {
      const newSession: AISessionState = {
        sessionId: `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        messages: [],
        isProcessing: false,
      };
      setSession(newSession);
    }
  }, [session]);

  const openWithMessage = useCallback((assistantGreeting: string) => {
    const greetMsg: AIChatMessage = {
      id: `msg_${Date.now()}_assistant`,
      role: 'assistant',
      content: assistantGreeting,
      timestamp: new Date(),
    };

    if (session) {
      setSession((prev) =>
        prev ? { ...prev, messages: [...prev.messages, greetMsg] } : prev
      );
      setIsOpen(true);
      return;
    }

    setSession({
      sessionId: `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      messages: [greetMsg],
      isProcessing: false,
    });
    setIsOpen(true);
  }, [session]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleChat = useCallback(() => {
    if (isOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [isOpen, openChat, closeChat]);

  const addMessage = useCallback((message: AIChatMessage) => {
    setSession((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [...prev.messages, message],
      };
    });
  }, []);

  const addAIMessage = useCallback((content: string, actions?: AIAction[]) => {
    const msg: AIChatMessage = {
      id: `msg_${Date.now()}_ai`,
      role: 'assistant',
      content,
      timestamp: new Date(),
      actions,
    };
    setSession((prev) =>
      prev ? { ...prev, messages: [...prev.messages, msg] } : prev
    );
  }, []);

  const saveActivePreview = useCallback(async (): Promise<string | null> => {
    if (!session?.activePreview || !onSave) return null;
    try {
      return await onSave(
        session.activePreview,
        session.conversationContext?.reportType || 'report'
      );
    } catch (error) {
      console.error('[AIChatContext] Failed to save AI report draft:', error);
      return null;
    }
  }, [session, onSave]);

  const startReportCreation = useCallback((reportType: 'report' | 'dashboard' | 'document', promptMessage: string) => {
    const promptMsg: AIChatMessage = {
      id: `msg_${Date.now()}_prompt`,
      role: 'assistant',
      content: promptMessage,
      timestamp: new Date(),
    };
    if (session) {
      setSession((prev) =>
        prev
          ? { ...prev, messages: [...prev.messages, promptMsg], conversationContext: { ...prev.conversationContext, reportType } }
          : prev
      );
    } else {
      setSession({
        sessionId: `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        messages: [promptMsg],
        isProcessing: false,
        conversationContext: { reportType },
      });
    }
    setIsOpen(true);
  }, [session]);

  const sendMessage = useCallback(async (message: string) => {
    if (!session) return;

    const userMessage: AIChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    addMessage(userMessage);

    setSession((prev) => {
      if (!prev) return prev;
      return { ...prev, isProcessing: true, error: undefined };
    });

    try {
      const response = await aiChatService.sendMessage(message, {
        reportType: session.conversationContext?.reportType,
        conversationId: session.conversationContext?.conversationId,
      });

      const assistantMessage: AIChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        actions: response.actions,
        tokensUsed: response.tokensUsed,
      };

      addMessage(assistantMessage);

      const previewAction = response.actions?.find((a) => a.type === 'preview-config');
      if (previewAction && previewAction.type === 'preview-config') {
        // Transform response to handle both payload.config and config structures
        let config = (previewAction as any).config || (previewAction as any).payload?.config;
        
        // If config has widgets but no dashboardConfig, wrap it
        if (config?.widgets && !config?.dashboardConfig) {
          // Add default layout to widgets that don't have it
          const widgetsWithLayout = (config.widgets || []).map((widget: any, index: number) => {
            if (!widget.layout) {
              const widgetType = widget.type?.toLowerCase();
              const isKPI = widgetType === 'kpi';
              const defaultW = isKPI ? 3 : 6;
              const defaultH = isKPI ? 2 : 4;
              const x = (index * defaultW) % 12;
              const y = Math.floor((index * defaultW) / 12) * defaultH;
              
              return {
                ...widget,
                id: widget.id || `widget-${Date.now()}-${index}`,
                layout: { x, y, w: defaultW, h: defaultH }
              };
            }
            return {
              ...widget,
              id: widget.id || `widget-${Date.now()}-${index}`
            };
          });
          
          config = {
            name: config.name || 'AI Generated Dashboard',
            type: session?.conversationContext?.reportType || 'dashboard',
            dashboardConfig: {
              widgets: widgetsWithLayout,
              layout: config.layout || { columns: 12, rowHeight: 60 }
            }
          };
        }
        
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            isProcessing: false,
            activePreview: config,
            conversationContext: {
              ...prev.conversationContext,
              reportType: (config as any)?.type || prev.conversationContext?.reportType,
              lastAction: previewAction,
              conversationId: response.conversationId || prev.conversationContext?.conversationId,
            },
          };
        });
      } else {
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            isProcessing: false,
            conversationContext: {
              ...prev.conversationContext,
              lastAction: response.actions?.[0],
              conversationId: response.conversationId || prev.conversationContext?.conversationId,
            },
          };
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          isProcessing: false,
          error: errorMessage,
        };
      });

      const errorChatMessage: AIChatMessage = {
        id: `msg_${Date.now()}_error`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: new Date(),
      };

      addMessage(errorChatMessage);
    }
  }, [session, addMessage]);

  const generateReport = useCallback(async (request: AIGenerateReportRequest) => {
    if (!session) return;

    const userMessage: AIChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: request.description,
      timestamp: new Date(),
    };

    addMessage(userMessage);

    setSession((prev) => {
      if (!prev) return prev;
      return { ...prev, isProcessing: true, error: undefined };
    });

    try {
      const response = await aiChatService.generateReport(request);

      const assistantMessage: AIChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        actions: response.actions,
        tokensUsed: response.tokensUsed,
      };

      addMessage(assistantMessage);

      const previewAction = response.actions?.find((a) => a.type === 'preview-config');
      if (previewAction && previewAction.type === 'preview-config') {
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            isProcessing: false,
            activePreview: previewAction.config,
            conversationContext: {
              reportType: request.reportType,
              lastAction: previewAction,
              conversationId: response.conversationId || prev.conversationContext?.conversationId,
            },
          };
        });
      } else {
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            isProcessing: false,
            conversationContext: {
              reportType: request.reportType,
              lastAction: response.actions?.[0],
              conversationId: response.conversationId || prev.conversationContext?.conversationId,
            },
          };
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      setSession((prev) => {
        if (!prev) return prev;
        return { ...prev, isProcessing: false, error: errorMessage };
      });

      addMessage({
        id: `msg_${Date.now()}_error`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: new Date(),
      });
    }
  }, [session, addMessage]);

  const refineConfig = useCallback(async (request: AIRefineConfigRequest) => {
    if (!session) return;

    const userMessage: AIChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: request.refinementPrompt,
      timestamp: new Date(),
    };

    addMessage(userMessage);

    setSession((prev) => {
      if (!prev) return prev;
      return { ...prev, isProcessing: true, error: undefined };
    });

    try {
      const response = await aiChatService.refineConfig(request);

      const assistantMessage: AIChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        actions: response.actions,
        tokensUsed: response.tokensUsed,
      };

      addMessage(assistantMessage);

      const applyChangeAction = response.actions?.find((a) => a.type === 'apply-change');
      if (applyChangeAction && applyChangeAction.type === 'apply-change') {
        setSession((prev) => {
          if (!prev || !prev.activePreview) return prev;
          
          const updatedConfig = {
            ...prev.activePreview,
            ...applyChangeAction.patch,
          };

          return {
            ...prev,
            isProcessing: false,
            activePreview: updatedConfig,
          };
        });
      } else {
        setSession((prev) => {
          if (!prev) return prev;
          return { ...prev, isProcessing: false };
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          isProcessing: false,
          error: errorMessage,
        };
      });
    }
  }, [session, addMessage]);

  const uploadPDF = useCallback(async (file: File) => {
    if (!session) return;

    const userMessage: AIChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: `Uploading PDF: ${file.name}`,
      timestamp: new Date(),
    };

    addMessage(userMessage);

    setSession((prev) => {
      if (!prev) return prev;
      return { ...prev, isProcessing: true, error: undefined };
    });

    try {
      const response = await aiChatService.uploadPDF(file);

      const assistantMessage: AIChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        actions: response.actions,
        tokensUsed: response.tokensUsed,
      };

      addMessage(assistantMessage);

      const previewAction = response.actions?.find((a) => a.type === 'preview-config');
      if (previewAction && previewAction.type === 'preview-config') {
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            isProcessing: false,
            activePreview: previewAction.config,
            conversationContext: {
              reportType: 'document',
              lastAction: previewAction,
              conversationId: response.conversationId,
            },
          };
        });
      } else {
        setSession((prev) => {
          if (!prev) return prev;
          return { ...prev, isProcessing: false };
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload PDF';
      
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          isProcessing: false,
          error: errorMessage,
        };
      });

      addMessage({
        id: `msg_${Date.now()}_error`,
        role: 'assistant',
        content: `Sorry, I encountered an error uploading the PDF: ${errorMessage}`,
        timestamp: new Date(),
      });
    }
  }, [session, addMessage]);

  const analyzePDF = useCallback(async (request: AIAnalyzePDFRequest) => {
    if (!session) return;

    const userMessage: AIChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: `Analyzing PDF: ${request.fileName}`,
      timestamp: new Date(),
    };

    addMessage(userMessage);  

    setSession((prev) => {
      if (!prev) return prev;
      return { ...prev, isProcessing: true, error: undefined };
    });

    try {
      const response = await aiChatService.analyzePDF(request);

      const assistantMessage: AIChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        actions: response.actions,
        tokensUsed: response.tokensUsed,
      };

      addMessage(assistantMessage);

      const previewAction = response.actions?.find((a) => a.type === 'preview-config');
      if (previewAction && previewAction.type === 'preview-config') {
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            isProcessing: false,
            activePreview: previewAction.config,
            conversationContext: {
              reportType: 'document',
              lastAction: previewAction,
            },
          };
        });
      } else {
        setSession((prev) => {
          if (!prev) return prev;
          return { ...prev, isProcessing: false };
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          isProcessing: false,
          error: errorMessage,
        };
      });
    }
  }, [session, addMessage]);

  const clearSession = useCallback(() => {
    aiChatService.clearSession();
    setSession({
      sessionId: `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      messages: [],
      isProcessing: false,
    });
  }, []);

  const applyAction = useCallback((action: AIAction) => {
    switch (action.type) {
      case 'preview-config':
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            activePreview: action.config,
          };
        });
        break;
      
      case 'apply-change':
        setSession((prev) => {
          if (!prev || !prev.activePreview) return prev;
          
          const updatedConfig = {
            ...prev.activePreview,
            ...action.patch,
          };

          return {
            ...prev,
            activePreview: updatedConfig,
          };
        });
        break;
      
      case 'navigate':
        break;
      
      case 'open-report':
        break;
      
      case 'recommend-existing':
        break;
    }
  }, []);

  const value: AIChatContextValue = {
    isOpen,
    session,
    openChat,
    openWithMessage,
    closeChat,
    toggleChat,
    sendMessage,
    generateReport,
    refineConfig,
    uploadPDF,
    analyzePDF,
    clearSession,
    applyAction,
    saveActivePreview,
    addAIMessage,
    startReportCreation,
    onNavigate,
    appContext: appContext || 'tenant',
  };

  return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
}

export function useAIChat(): AIChatContextValue {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChat must be used within AIChatProvider');
  }
  return context;
}
